import { OrderStatusEnum, UserRoleEnum } from '@authority-labs/enums';

/**
 * Statuses in which the certificate sits with an approver and has not been
 * approved yet.
 */
export const APPROVAL_ORDER_STATUSES: OrderStatusEnum[] = [
  OrderStatusEnum.WaitingForYarivApproval,
  OrderStatusEnum.WaitingForAkivaApproval,
  OrderStatusEnum.WaitingForRoiApproval
];

/**
 * Order statuses a tester / senior-tester is allowed to move an order between.
 * Anything outside this list is off limits for them.
 */
export const TESTER_ALLOWED_ORDER_STATUSES: OrderStatusEnum[] = [
  OrderStatusEnum.WaitingForInspection,
  OrderStatusEnum.InspectionInProgress,
  OrderStatusEnum.InspectionInProgressNoPlans,
  OrderStatusEnum.InspectionInProgressPartialPlans,
  OrderStatusEnum.WaitingForCustomerDocs,
  OrderStatusEnum.WaitingForReview,
  ...APPROVAL_ORDER_STATUSES
];

/**
 * Once the certificate is waiting for an approver, or for its reviewer, the
 * order is out of the assignee's hands - they can no longer change its status.
 */
export const TESTER_LOCKED_ORDER_STATUSES: OrderStatusEnum[] = [
  OrderStatusEnum.WaitingForReview,
  ...APPROVAL_ORDER_STATUSES
];

/**
 * Where the reviewer may move an order that waits for review: back to the
 * inspection, or on to an approver.
 */
export const REVIEW_EXIT_ORDER_STATUSES: OrderStatusEnum[] = [
  OrderStatusEnum.InspectionInProgress,
  OrderStatusEnum.InspectionInProgressNoPlans,
  OrderStatusEnum.InspectionInProgressPartialPlans,
  ...APPROVAL_ORDER_STATUSES
];

const TESTER_ROLES: string[] = [UserRoleEnum.Tester, UserRoleEnum.SeniorTester];

export function isTesterRole(role: string): boolean {
  return TESTER_ROLES.includes(role);
}

/**
 * Whether a tester may change the status of an order that currently sits on
 * `currentStatusKey`. Both the current status and the target must be within
 * {@link TESTER_ALLOWED_ORDER_STATUSES}, and the approval statuses are terminal.
 */
export function canTesterChangeOrderStatus(currentStatusKey: string): boolean {
  const key = currentStatusKey as OrderStatusEnum;
  return (
    TESTER_ALLOWED_ORDER_STATUSES.includes(key) &&
    !TESTER_LOCKED_ORDER_STATUSES.includes(key)
  );
}

/** Whether a tester may select `statusKey` as the new status of an order. */
export function canTesterUseOrderStatus(statusKey: string): boolean {
  return TESTER_ALLOWED_ORDER_STATUSES.includes(statusKey as OrderStatusEnum);
}

/**
 * Whether a tester may change the status of this order at all: the reviewer
 * while it waits for review, otherwise the assignee while its status is
 * changeable.
 */
export function canTesterChangeStatusOf(params: {
  assignee: string;
  reviewedBy?: string | null;
  username: string;
  currentStatusKey: string;
}): boolean {
  const { assignee, reviewedBy, username, currentStatusKey } = params;
  if (currentStatusKey === OrderStatusEnum.WaitingForReview)
    return !!reviewedBy && reviewedBy === username;
  return (
    !!assignee &&
    assignee === username &&
    canTesterChangeOrderStatus(currentStatusKey)
  );
}

/** The statuses a tester may pick next, given where the order is now. */
export function testerTargetStatuses(
  currentStatusKey: string
): OrderStatusEnum[] {
  return currentStatusKey === OrderStatusEnum.WaitingForReview
    ? REVIEW_EXIT_ORDER_STATUSES
    : TESTER_ALLOWED_ORDER_STATUSES;
}

/**
 * Full rule for a tester: they may change this order's status (see
 * canTesterChangeStatusOf) and the target is one they may pick.
 */
export function isTesterStatusTransitionAllowed(params: {
  assignee: string;
  reviewedBy?: string | null;
  username: string;
  currentStatusKey: string;
  newStatusKey: string;
}): boolean {
  const { currentStatusKey, newStatusKey } = params;
  return (
    canTesterChangeStatusOf(params) &&
    testerTargetStatuses(currentStatusKey).includes(
      newStatusKey as OrderStatusEnum
    )
  );
}
